/**
 * LifecycleRail — the one drawing of "where is it" (docs/features/
 * journey-visual-language/01_proposal.md §2–§3). Green is done, grey is in
 * progress, light grey is to do, and a red or amber dot is a problem.
 *
 * - One row of equal-width steps: the name above the node, one or two short
 *   status lines below. No phase headings, no band.
 * - Nodes: done is the success fill with a white check; in progress is a
 *   half-filled muted-grey node (several may be in progress at once); to do
 *   is hollow in the border colour. A problem is a small red (late) or amber
 *   (at risk) dot on the node, and its one note line is the only other
 *   colour. The brand pink is not used on the rail.
 * - A 2px track, solid muted up to the first step that is not done, dashed
 *   in the border colour after it.
 * - A step covering several items (Production's lines, Delivery's lines, the
 *   invoices) has a ▾ after its status; it opens one detail panel directly
 *   under the rail. Only one panel is open at a time.
 * - A page may give a step its own panel (`renderPanel`): the order page's
 *   steps hold its lines, production and delivery (Ben, 3 Oct), drawn in the
 *   same rows as the detail (`RailRow`) with their buttons and an ⓘ for the
 *   rest; a planned step's panel says it is not built yet. The page may hold
 *   which step is open (`openStepId`), so it can live in the URL.
 * - A step owned by another object links its first status line to that page.
 * - A step that needs explaining carries an ⓘ beside its name.
 */
import {
  useId,
  useState,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import { Link } from "react-router";
import { Check, ChevronDown } from "lucide-react";
import type {
  LifecycleRail as LifecycleRailModel,
  RailDetailItem,
  RailLink,
  RailStep,
  StepProblem,
  StepState,
} from "@shared-core/domain/lifecycle-rail";
import { paths } from "@shared-core/routing/routes";
import { cn } from "../primitives/utils";
import { InfoTip } from "./InfoTip";

const STATE_WORDS: Record<StepState, string> = {
  done: "done",
  in_progress: "in progress",
  to_do: "to do",
};

const PROBLEM_WORDS: Record<StepProblem, string> = {
  late: "late",
  at_risk: "at risk",
};

const PROBLEM_TEXT: Record<StepProblem, string> = {
  late: "text-red-700 dark:text-red-400",
  at_risk: "text-amber-700 dark:text-amber-400",
};

/** Steps to a row on a phone: the width a step's status lines need. */
const PHONE_COLUMNS = 3;

const FOCUS =
  "rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

function hrefOf(link: RailLink): string {
  switch (link.kind) {
    case "estimate":
      return paths.estimateDetail({ id: link.estimateId });
    case "purchase_order":
      return paths.purchaseOrderDetail({
        purchaseOrderNo: link.purchaseOrderNo,
      });
    case "job":
      return paths.jobWorkspace({ jobNo: link.jobNo });
    case "order":
      return paths.orderDetail({ orderNo: link.orderNo });
    case "quote":
      return paths.quoteDetail({ quoteNo: link.quoteNo });
    case "object":
      return paths.objectRecord({ objectId: link.object, recordKey: link.id });
  }
}

/** "in progress, late" — read after a step's name or a detail row. */
function spokenState(state: StepState, problem: StepProblem | null): string {
  return [STATE_WORDS[state], problem && PROBLEM_WORDS[problem]]
    .filter(Boolean)
    .join(", ");
}

function Node({
  state,
  problem,
  small = false,
}: {
  state: StepState;
  problem: StepProblem | null;
  small?: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "relative z-10 flex shrink-0 items-center justify-center rounded-full",
        small ? "size-3" : "size-4",
        state === "done" && "bg-success text-white",
        state === "in_progress" &&
          "border-2 border-muted-foreground bg-[linear-gradient(90deg,var(--muted-foreground)_50%,var(--card)_50%)]",
        state === "to_do" && "border-2 border-border bg-card",
      )}
    >
      {state === "done" && (
        <Check className={small ? "size-2" : "size-2.5"} strokeWidth={3} />
      )}
      {problem && (
        <span
          className={cn(
            "absolute -right-1 -top-1 rounded-full ring-2 ring-card",
            small ? "size-1.5" : "size-2",
            problem === "late" ? "bg-red-600" : "bg-amber-500",
          )}
        />
      )}
    </span>
  );
}

function Track({
  side,
  solid,
  hiddenOnPhone,
}: {
  side: "left" | "right";
  solid: boolean;
  /** At the end of a wrapped row on a phone, there is no neighbour to join. */
  hiddenOnPhone: boolean;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        "absolute top-[7px]",
        hiddenOnPhone && "max-sm:hidden",
        side === "left" ? "left-0 right-1/2" : "left-1/2 right-0",
        solid
          ? "h-0.5 bg-muted-foreground/60"
          : "h-0 border-t-2 border-dashed border-border",
      )}
    />
  );
}

/** A step's status lines; the first links its object, and a ▾ ends them when it has detail. */
function StepStatus({
  step,
  isOpen,
  panelId,
  onToggle,
}: {
  step: RailStep;
  isOpen: boolean;
  panelId: string;
  onToggle: (() => void) | null;
}) {
  const [first, ...rest] = step.status;
  const lineClass = "block max-w-full text-xs text-muted-foreground";
  const statusLines = (
    <>
      {first &&
        (step.link && !onToggle ? (
          <Link
            to={hrefOf(step.link)}
            className={cn(
              lineClass,
              "underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-current",
              FOCUS,
            )}
          >
            {first}
          </Link>
        ) : (
          <span className={lineClass}>{first}</span>
        ))}
      {rest.map((line) => (
        <span key={line} className={lineClass}>
          {line}
        </span>
      ))}
    </>
  );
  if (!onToggle) return statusLines;
  return (
    <button
      type="button"
      aria-expanded={isOpen}
      aria-controls={isOpen ? panelId : undefined}
      onClick={onToggle}
      className={cn(
        "flex max-w-full flex-col items-center font-normal hover:[&>span]:text-foreground",
        isOpen && "[&>span]:text-foreground",
        FOCUS,
      )}
    >
      <span className="sr-only">{step.name}: </span>
      {step.status.map((line, i) => (
        <span key={line} className={cn(lineClass, "transition-colors")}>
          {line}
          {i === step.status.length - 1 && (
            <ChevronDown
              aria-hidden
              className={cn(
                "ml-0.5 inline size-3.5 align-[-3px] transition-transform",
                isOpen && "rotate-180",
              )}
            />
          )}
        </span>
      ))}
      <span className="sr-only">, {isOpen ? "hide" : "show"} detail</span>
    </button>
  );
}

/**
 * One row of a step's detail, in the rail's own drawing: a small node, a
 * muted label, the name, a short status, and (on a page's own panel) the
 * row's buttons and an ⓘ at its end that opens its detail. One line on
 * desktop; below md, two: label and name, then status and buttons. A
 * focused row (opened at it from a link) has a quiet left border.
 */
export function RailRow({
  state,
  problem,
  label,
  name,
  spokenName,
  status,
  actions,
  detail,
  expanded,
  isFocused = false,
  rowRef,
}: {
  state: StepState;
  problem: StepProblem | null;
  /** ".01 · Bristol", "PO-K20417". */
  label: ReactNode;
  name: ReactNode;
  /** The name read out with the row's state, when `name` is not plain text. */
  spokenName?: string;
  status: ReactNode;
  /** The row's step: one outlined button. */
  actions?: ReactNode;
  /** The ⓘ that opens the row's detail. */
  detail?: ReactNode;
  /** Opened under the row, across its full width (a row that expands in place). */
  expanded?: ReactNode;
  isFocused?: boolean;
  rowRef?: Ref<HTMLLIElement>;
}) {
  return (
    <li
      ref={rowRef}
      data-focused={isFocused || undefined}
      className={cn(
        "grid items-center gap-x-3 gap-y-1 border-l-2 py-2 pl-2 text-sm",
        "grid-cols-[auto_minmax(0,1fr)_1.75rem] md:grid-cols-[auto_7rem_minmax(0,14rem)_minmax(0,1fr)_auto_1.75rem]",
        isFocused ? "border-l-foreground/40" : "border-l-transparent",
      )}
    >
      <Node state={state} problem={problem} small />
      {/* Below md these wrappers make the row's two lines; on md they vanish
          and their children are the grid's own columns. */}
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 md:contents">
        <span className="text-xs tabular-nums text-muted-foreground md:truncate">
          {label}
        </span>
        <span className="min-w-0 font-medium text-foreground md:truncate">
          {name}
          <span className="sr-only">
            {spokenName ? `: ${spokenName}` : ""}, {spokenState(state, problem)}
          </span>
        </span>
      </span>
      {/* Below md the buttons sit beside the status, or under it when the
          status would be squeezed narrower than ~11rem. */}
      <span className="col-span-2 col-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 md:contents">
        <span
          className={cn(
            "min-w-0 flex-[1_1_11rem] text-xs",
            problem ? PROBLEM_TEXT[problem] : "text-muted-foreground",
          )}
        >
          {status}
        </span>
        <span className="ml-auto flex flex-wrap items-center justify-end gap-1 empty:hidden md:empty:flex">
          {actions}
        </span>
      </span>
      <span className="col-start-3 row-start-1 flex justify-end md:col-start-auto md:row-start-auto">
        {detail}
      </span>
      {expanded && <div className="col-span-full pb-2 pl-5">{expanded}</div>}
    </li>
  );
}

/** A step's rows. */
export function RailRows({
  ariaLabel,
  children,
}: {
  ariaLabel?: string;
  children: ReactNode;
}) {
  return (
    <ul aria-label={ariaLabel} className="divide-y divide-border">
      {children}
    </ul>
  );
}

function DetailPanel({ step }: { step: RailStep }) {
  return (
    <RailRows>
      {step.detail.map((item: RailDetailItem) => (
        <RailRow
          key={item.id}
          state={item.state}
          problem={item.problem}
          label={item.label}
          name={
            item.link ? (
              <Link
                to={hrefOf(item.link)}
                className={cn("hover:underline", FOCUS)}
              >
                {item.name}
              </Link>
            ) : (
              item.name
            )
          }
          spokenName={item.link ? item.name : undefined}
          status={item.status}
        />
      ))}
    </RailRows>
  );
}

export function LifecycleRail({
  rail,
  ariaLabel,
  renderPanel,
  openStepId: controlledOpenStepId,
  onOpenStepChange,
}: {
  rail: LifecycleRailModel;
  ariaLabel: string;
  /**
   * A step's own panel, in place of its detail rows; null when it has none.
   * A step with a panel always opens, whatever its detail.
   */
  renderPanel?: (step: RailStep) => ReactNode | null;
  /** The open step, when the page holds it (in the URL, say). */
  openStepId?: string | null;
  onOpenStepChange?: (stepId: string | null) => void;
}) {
  const [ownOpenStepId, setOwnOpenStepId] = useState<string | null>(null);
  const isControlled = controlledOpenStepId !== undefined;
  const openStepId = isControlled ? controlledOpenStepId : ownOpenStepId;
  const setOpenStepId = (id: string | null) => {
    if (!isControlled) setOwnOpenStepId(id);
    onOpenStepChange?.(id);
  };
  const panelOf = (step: RailStep) => renderPanel?.(step) ?? null;
  const idBase = useId();
  const panelId = (stepId: string) => `${idBase}-${stepId}-detail`;
  const steps = rail.steps;
  // The track is solid up to the first step that is not done.
  const firstNotDone = steps.findIndex((s) => s.state !== "done");
  const solidUpTo = firstNotDone === -1 ? steps.length - 1 : firstNotDone;
  const openStep = steps.find((s) => s.id === openStepId) ?? null;

  return (
    <div role="group" aria-label={ariaLabel}>
      {/* `relative` makes the scroller the containing block of the steps'
          absolutely positioned sr-only text, so it scrolls with the rail
          instead of widening the page on a narrow screen. From sm up the
          steps are one row (scrolling if there are too many to fit); on a
          phone they wrap, PHONE_COLUMNS to a row, so every step shows. */}
      <div className="relative overflow-x-auto">
        <ol
          className={cn(
            "grid w-full grid-cols-3 gap-y-4 py-1",
            "sm:min-w-[calc(var(--rail-steps)*6.5rem)] sm:grid-cols-[repeat(var(--rail-steps),minmax(0,1fr))] sm:gap-y-0",
          )}
          style={{ "--rail-steps": steps.length } as CSSProperties}
        >
          {steps.map((step, i) => {
            const expandable = panelOf(step) !== null || step.detail.length > 1;
            const isOpen = openStepId === step.id;
            return (
              <li
                key={step.id}
                className="flex min-w-0 flex-col items-center text-center"
              >
                <span
                  className={cn(
                    "block h-5 max-w-full truncate px-1 text-sm font-medium leading-5",
                    step.state === "to_do"
                      ? "text-muted-foreground"
                      : "text-foreground",
                  )}
                >
                  {step.name}
                  <span className="sr-only">
                    , {spokenState(step.state, step.problem)}
                  </span>
                  {step.info && (
                    <InfoTip
                      label={`About ${step.name}`}
                      className="ml-1 align-[-2px]"
                    >
                      {step.info}
                    </InfoTip>
                  )}
                </span>
                <span className="relative mt-1.5 flex w-full justify-center">
                  {i > 0 && (
                    <Track
                      side="left"
                      solid={i <= solidUpTo}
                      hiddenOnPhone={i % PHONE_COLUMNS === 0}
                    />
                  )}
                  {i < steps.length - 1 && (
                    <Track
                      side="right"
                      solid={i < solidUpTo}
                      hiddenOnPhone={i % PHONE_COLUMNS === PHONE_COLUMNS - 1}
                    />
                  )}
                  <Node state={step.state} problem={step.problem} />
                </span>
                <span className="mt-1.5 flex max-w-full flex-col items-center px-1">
                  <StepStatus
                    step={step}
                    isOpen={isOpen}
                    panelId={panelId(step.id)}
                    onToggle={
                      expandable
                        ? () => setOpenStepId(isOpen ? null : step.id)
                        : null
                    }
                  />
                  {step.alert && step.problem && (
                    <span
                      className={cn(
                        "block max-w-full text-xs",
                        PROBLEM_TEXT[step.problem],
                      )}
                    >
                      {step.alert}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
      {openStep && (panelOf(openStep) || openStep.detail.length > 1) && (
        <div
          id={panelId(openStep.id)}
          role="region"
          aria-label={`${openStep.name} detail`}
          className="mt-3 border-t border-border pt-3"
        >
          {panelOf(openStep) ?? <DetailPanel step={openStep} />}
        </div>
      )}
    </div>
  );
}
